<template>
  <FormOverlay>
    <div class="overflow-auto w-full flex flex-col h-full justify-between space-y-8 py-8">
      <h1 class="text-accent text-center text-xl font-semibold">{{ $t('addYourActivity') }}</h1>

      <div class="mx-auto max-w-sm w-full">
        <div>
          <label class="label">
            <span class="label-text">{{ $t('email') }}</span>
          </label>
          <input
            v-model="email"
            type="text"
            :placeholder="$t('typeHere')"
            class="input input-bordered w-full"
          />
        </div>

        <div>
          <label class="label">
            <span class="label-text">{{ $t('title') }}</span>
          </label>
          <input
            v-model="title"
            type="text"
            :placeholder="$t('typeHere')"
            class="input input-bordered w-full"
          />
        </div>

        <div>
          <label class="label">
            <span class="label-text">{{ $t('locationName') }}</span>
          </label>
          <input
            v-model="location"
            type="text"
            :placeholder="$t('typeHere')"
            class="input input-bordered w-full"
          />
        </div>

        <div>
          <label class="label">
            <span class="label-text">{{ $t('description') }}</span>
          </label>
          <textarea
            v-model="description"
            class="textarea textarea-bordered w-full"
            :placeholder="$t('typeHere')"
          />
        </div>

        <div>
          <label class="label">
            <span class="label-text">{{ $t('uploadFile') }}</span>
          </label>
          <input
            type="file"
            class="file-input file-input-bordered file-input-primary file-input-sm w-full h-[1.99rem]"
            multiple
            accept="image/*,video/mp4"
            @change="onFileChanged"
          />
        </div>
      </div>

      <div class="mx-auto max-w-sm w-full">
        <button class="btn btn-primary w-full text-accent" :disabled="!canSubmit" @click="onSubmit">
          {{ $t('send') }}
        </button>
        <button class="btn btn-error w-full mt-4" @click="onCancel">{{ $t('cancel') }}</button>
      </div>
    </div>
  </FormOverlay>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { useI18n } from 'vue-i18n'
import { useToast } from 'vue-toast-notification'

import { axiosInstance } from '@/utils/axios'

import FormOverlay from '@/components/FormOverlay.vue'

const { t } = useI18n()

const toast = useToast({ position: 'top-right' })

const email = ref('')
const title = ref('')
const location = ref('')
const description = ref('')
const files = ref<File[]>([])

export interface SubmitFormProps {
  x: number
  y: number
}

const props = withDefaults(defineProps<SubmitFormProps>(), {})

const emit = defineEmits<{
  (e: 'submit'): void
  (e: 'cancel'): void
}>()

const onFileChanged = (event: Event) => {
  const target = event.target as HTMLInputElement

  if (files.value.length > 20) {
    console.error('File limit exceeded.')
    return
  }

  if (target && target.files) {
    files.value.push(...target.files)
  }
}

const canSubmit = computed(() => {
  return Boolean(email.value) && Boolean(title.value) && Boolean(location.value)
})

const onSubmit = async () => {
  if (!email.value && !title.value) {
    return
  }

  const data = new FormData()

  data.append('email', email.value)
  data.append('title', title.value)
  data.append('location', location.value)
  data.append('description', description.value)
  data.append('x', String(props.x))
  data.append('y', String(props.y))

  for (let i = 0; i < files.value.length; i++) {
    data.append(`file${i}`, files.value[i])
  }

  try {
    await axiosInstance.post('activities', data)
    toast.success(t('activitySubmit'))

    emit('submit')
  } catch (error) {
    if (error instanceof Error) {
      console.error('message:', error.message)
    }
  }
}

const onCancel = () => {
  emit('cancel')
}
</script>
